import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Plus, FileText, Search, Filter, MoreVertical,
  Send, CheckCircle, Clock, AlertCircle, Trash2,
  Download, Edit, Eye, ArrowLeft, X, Import, ChevronDown
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200', dot: 'bg-slate-400' },
  sent: { label: 'Sent', color: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700', dot: 'bg-blue-400' },
  paid: { label: 'Paid', color: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700', dot: 'bg-emerald-400' },
  overdue: { label: 'Overdue', color: 'bg-red-50 dark:bg-red-900/30 text-red-700', dot: 'bg-red-400' },
};

// ---- Invoice List View ----
const InvoiceList = ({ invoices, onView, onCreate, onStatusChange, onDelete, loading, companyCurrency = 'INR' }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);

  const filtered = invoices.filter(inv => {
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return inv.invoice_number.toLowerCase().includes(q) ||
        inv.client_name.toLowerCase().includes(q);
    }
    return true;
  });

  const stats = {
    total: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
    totalAmount: invoices.filter(i => i.status !== 'draft').reduce((s, i) => s + (i.total * (i.exchange_rate || 1)), 0),
    paidAmount: invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total * (i.exchange_rate || 1)), 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="invoices-title">Invoices</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Create and manage invoices from tracked time</p>
        </div>
        <button
          onClick={onCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors"
          data-testid="create-invoice-btn"
        >
          <Plus className="w-4 h-4" />
          Create Invoice
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Total Invoiced</p>
          <p className="text-xl font-bold text-slate-900 dark:text-white mt-1">{formatCurrency(stats.totalAmount, companyCurrency)}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Collected</p>
          <p className="text-xl font-bold text-emerald-600 mt-1">{formatCurrency(stats.paidAmount, companyCurrency)}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Outstanding</p>
          <p className="text-xl font-bold text-amber-600 mt-1">{formatCurrency(stats.totalAmount - stats.paidAmount, companyCurrency)}</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Total Invoices</p>
          <p className="text-xl font-bold text-slate-900 dark:text-white mt-1">{stats.total}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            data-testid="invoice-search"
          />
        </div>
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-700 rounded-lg p-0.5">
          {['all', 'draft', 'sent', 'paid', 'overdue'].map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterStatus === s ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:text-slate-200'
              }`}
              data-testid={`filter-${s}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
              {s !== 'all' && <span className="ml-1 text-slate-400">({stats[s] || 0})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Invoice Table */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading invoices...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 dark:text-slate-400 font-medium">No invoices found</p>
            <p className="text-sm text-slate-400 mt-1">Create your first invoice to get started</p>
            <button onClick={onCreate} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus className="w-4 h-4 inline mr-1" />Create Invoice
            </button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-700">
                <th className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase px-4 py-3">Invoice</th>
                <th className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase px-4 py-3">Client</th>
                <th className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase px-4 py-3">Date</th>
                <th className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase px-4 py-3">Due Date</th>
                <th className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase px-4 py-3">Amount</th>
                <th className="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(inv => (
                <tr key={inv.id} className="border-b border-slate-50 hover:bg-slate-50 dark:bg-slate-700/50 cursor-pointer" onClick={() => onView(inv)}>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-sm text-slate-900 dark:text-white">{inv.invoice_number}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{inv.client_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{inv.issue_date}</td>
                  <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{inv.due_date || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[inv.status]?.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[inv.status]?.dot}`}></span>
                      {STATUS_CONFIG[inv.status]?.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-sm text-slate-900 dark:text-white">
                    {formatCurrency(inv.total, inv.currency)}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                    <div className="relative inline-block">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === inv.id ? null : inv.id)}
                        className="p-1 hover:bg-slate-100 dark:bg-slate-700 rounded"
                        data-testid={`invoice-menu-${inv.id}`}
                      >
                        <MoreVertical className="w-4 h-4 text-slate-400" />
                      </button>
                      {openMenuId === inv.id && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setOpenMenuId(null)} />
                          <div className="absolute right-0 bottom-full mb-1 z-40 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 w-44" data-testid={`invoice-dropdown-${inv.id}`}>
                            <button onClick={() => { onView(inv); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                              <Eye className="w-3.5 h-3.5" /> View
                            </button>
                            {inv.status === 'draft' && (
                              <button onClick={() => { onStatusChange(inv.id, 'sent'); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                                <Send className="w-3.5 h-3.5" /> Mark as Sent
                              </button>
                            )}
                            {inv.status === 'sent' && (
                              <button onClick={() => { onStatusChange(inv.id, 'paid'); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2">
                                <CheckCircle className="w-3.5 h-3.5" /> Mark as Paid
                              </button>
                            )}
                            <button onClick={() => { onDelete(inv.id); setOpenMenuId(null); }} className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 dark:bg-red-900/30 text-red-600 flex items-center gap-2" data-testid={`delete-invoice-${inv.id}`}>
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ---- Create / Edit Invoice Form ----
const InvoiceForm = ({ invoice, onSave, onCancel, token, companyCurrency = 'INR' }) => {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: invoice?.client_id || '',
    client_name: invoice?.client_name || '',
    invoice_number: invoice?.invoice_number || '',
    issue_date: invoice?.issue_date || new Date().toISOString().split('T')[0],
    due_date: invoice?.due_date || '',
    tax_label: invoice?.tax_label || 'GST',
    tax_rate: invoice?.tax_rate || 18,
    discount_percent: invoice?.discount_percent || 0,
    currency: invoice?.currency || 'INR',
    exchange_rate: invoice?.exchange_rate || 1,
    notes: invoice?.notes || '',
    from_name: invoice?.from_name || '',
    from_email: invoice?.from_email || '',
    from_address: invoice?.from_address || '',
    from_gstin: invoice?.from_gstin || '',
    from_logo: invoice?.from_logo || '',
    to_name: invoice?.to_name || '',
    to_email: invoice?.to_email || '',
    to_address: invoice?.to_address || '',
    to_gstin: invoice?.to_gstin || '',
    items: invoice?.items || [],
  });
  const [showImport, setShowImport] = useState(false);
  const [importData, setImportData] = useState({ entries: [], projects: [] });
  const [importLoading, setImportLoading] = useState(false);
  const [importDateFrom, setImportDateFrom] = useState('');
  const [importDateTo, setImportDateTo] = useState('');
  const [importGroupBy, setImportGroupBy] = useState('project');
  const [saving, setSaving] = useState(false);
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);

  const filteredImportEntries = selectedProjectIds.length > 0
    ? importData.entries.filter(e => selectedProjectIds.includes(e.project_id))
    : importData.entries;

  const toggleProjectFilter = (projectId) => {
    setSelectedProjectIds(prev =>
      prev.includes(projectId) ? prev.filter(id => id !== projectId) : [...prev, projectId]
    );
  };

  useEffect(() => {
    fetchClients();
    fetchWorkspaceSettings();
    if (!invoice) fetchNextNumber();
  }, []);

  const fetchWorkspaceSettings = async () => {
    try {
      const res = await fetch(`${API}/api/workspace-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const ws = await res.json();
        // Only auto-fill "From" if not editing an existing invoice
        if (!invoice) {
          const addr = [ws.billing_address_line1, ws.billing_address_line2, ws.billing_city, ws.billing_state, ws.billing_pincode, ws.billing_country].filter(Boolean).join('\n');
          setForm(f => ({
            ...f,
            from_name: f.from_name || ws.billing_company_name || ws.company_name || '',
            from_email: f.from_email || '',
            from_address: f.from_address || addr || '',
            from_gstin: f.from_gstin || ws.gst_number || '',
            from_logo: f.from_logo || ws.company_logo || '',
          }));
        }
      }
    } catch (e) { console.error(e); }
  };

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/admin/clients?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setClients(Array.isArray(data) ? data : data.clients || []);
    } catch (e) { console.error(e); }
  };

  const fetchNextNumber = async () => {
    try {
      const res = await fetch(`${API}/api/invoices/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setForm(f => ({ ...f, invoice_number: data.invoice_number }));
    } catch (e) { console.error(e); }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    const clientCurrency = client?.currency || 'INR';
    const isOverseas = client?.gst_treatment === 'overseas';
    setForm(f => ({
      ...f,
      client_id: clientId,
      client_name: client?.name || '',
      to_name: client?.contact_name || client?.name || '',
      to_email: client?.contact_email || '',
      to_address: client?.address || '',
      to_gstin: client?.gst_id || '',
      currency: clientCurrency,
      exchange_rate: clientCurrency === companyCurrency ? 1 : f.exchange_rate,
      // Overseas clients: no GST
      tax_rate: isOverseas ? 0 : f.tax_rate,
      tax_label: isOverseas ? 'Tax' : f.tax_label,
    }));
  };

  const fetchBillableEntries = async () => {
    if (!form.client_id) return;
    setImportLoading(true);
    try {
      const params = new URLSearchParams({ client_id: form.client_id });
      if (importDateFrom) params.append('from_date', importDateFrom);
      if (importDateTo) params.append('to_date', importDateTo);
      const res = await fetch(`${API}/api/invoices/billable-entries?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setImportData(data);
    } catch (e) { console.error(e); }
    setImportLoading(false);
  };

  const importTimeEntries = () => {
    const entries = filteredImportEntries;
    if (!entries.length) return;

    let newItems = [];
    if (importGroupBy === 'project') {
      const grouped = {};
      entries.forEach(e => {
        const key = e.project_id;
        if (!grouped[key]) grouped[key] = { hours: 0, rate: e.hourly_rate, name: e.project_name, ids: [] };
        grouped[key].hours += e.hours;
        grouped[key].ids.push(e.id);
      });
      Object.values(grouped).forEach(g => {
        newItems.push({
          type: 'time',
          description: g.name,
          project_name: g.name,
          quantity: parseFloat(g.hours.toFixed(2)),
          unit: 'hours',
          rate: g.rate,
          amount: parseFloat((g.hours * g.rate).toFixed(2)),
          time_entry_ids: g.ids,
        });
      });
    } else if (importGroupBy === 'member') {
      const grouped = {};
      entries.forEach(e => {
        const key = e.user_id;
        if (!grouped[key]) grouped[key] = { hours: 0, rate: e.hourly_rate, name: e.user_name, role: e.user_role || '', projects: new Set(), ids: [] };
        grouped[key].hours += e.hours;
        grouped[key].projects.add(e.project_name);
        grouped[key].ids.push(e.id);
      });
      Object.values(grouped).forEach(g => {
        const roleLabel = g.role ? ` (${g.role})` : '';
        const projectNames = [...g.projects].join(', ');
        newItems.push({
          type: 'time',
          description: `${g.name}${roleLabel}`,
          project_name: projectNames,
          user_name: g.name,
          quantity: parseFloat(g.hours.toFixed(2)),
          unit: 'hours',
          rate: g.rate,
          amount: parseFloat((g.hours * g.rate).toFixed(2)),
          time_entry_ids: g.ids,
        });
      });
    }
    setForm(f => ({ ...f, items: [...f.items, ...newItems] }));
    setShowImport(false);
  };

  const addManualItem = () => {
    setForm(f => ({
      ...f,
      items: [...f.items, { type: 'manual', description: '', quantity: 1, unit: 'units', rate: 0, amount: 0, time_entry_ids: [] }]
    }));
  };

  const addFixedFeeItem = () => {
    setForm(f => ({
      ...f,
      items: [...f.items, { type: 'fixed', description: '', quantity: 1, unit: 'fixed', rate: 0, amount: 0, time_entry_ids: [] }]
    }));
  };

  const updateItem = (index, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[index] = { ...items[index], [field]: value };
      if (field === 'quantity' || field === 'rate') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(items[index].quantity) || 0;
        const rate = field === 'rate' ? parseFloat(value) || 0 : parseFloat(items[index].rate) || 0;
        items[index].amount = parseFloat((qty * rate).toFixed(2));
      }
      if (field === 'amount') {
        items[index].amount = parseFloat(value) || 0;
      }
      return { ...f, items };
    });
  };

  const removeItem = (index) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== index) }));
  };

  const subtotal = form.items.reduce((s, i) => s + (i.amount || 0), 0);
  const discountAmt = form.discount_percent ? subtotal * form.discount_percent / 100 : 0;
  const taxableAmt = subtotal - discountAmt;
  const taxAmt = form.tax_rate ? taxableAmt * form.tax_rate / 100 : 0;
  const total = taxableAmt + taxAmt;

  const handleSave = async () => {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="p-2 hover:bg-slate-100 dark:bg-slate-700 rounded-lg" data-testid="back-to-invoices">
          <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{invoice ? 'Edit Invoice' : 'New Invoice'}</h1>
        </div>
        <button onClick={handleSave} disabled={saving} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm disabled:opacity-50" data-testid="save-invoice-btn">
          {saving ? 'Saving...' : 'Save Invoice'}
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: Invoice Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Invoice Details</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Invoice Number</label>
                <input value={form.invoice_number} onChange={e => setForm(f => ({ ...f, invoice_number: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  data-testid="invoice-number-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Client</label>
                <select value={form.client_id} onChange={e => handleClientChange(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  data-testid="invoice-client-select">
                  <option value="">Select a client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Issue Date</label>
                <input type="date" value={form.issue_date} onChange={e => setForm(f => ({ ...f, issue_date: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Due Date</label>
                <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Line Items</h3>
              <div className="flex gap-2">
                {form.client_id && (
                  <button onClick={() => { setShowImport(true); setSelectedProjectIds([]); fetchBillableEntries(); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 rounded-lg transition-colors"
                    data-testid="import-time-btn">
                    <Import className="w-3.5 h-3.5" /> Import Time
                  </button>
                )}
                <button onClick={addManualItem}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 rounded-lg transition-colors"
                  data-testid="add-manual-item-btn">
                  <Plus className="w-3.5 h-3.5" /> Add Item
                </button>
                <button onClick={addFixedFeeItem}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 rounded-lg transition-colors"
                  data-testid="add-fixed-fee-btn">
                  <Plus className="w-3.5 h-3.5" /> Fixed Fee
                </button>
              </div>
            </div>

            {form.items.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No items yet. Import time entries or add items manually.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase px-1">
                  <div className="col-span-2">Project</div>
                  <div className="col-span-3">Description</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-2 text-right">Amount</div>
                  <div className="col-span-1"></div>
                </div>
                {form.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-start bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2 pt-3">
                    <div className="col-span-2">
                      {item.type === 'time' ? (
                        <span className="text-sm text-slate-700 dark:text-slate-200 truncate block py-1.5" title={item.project_name}>{item.project_name || '—'}</span>
                      ) : (
                        <input value={item.project_name || ''} onChange={e => updateItem(i, 'project_name', e.target.value)}
                          placeholder="Project"
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:ring-1 focus:ring-blue-500/30" />
                      )}
                    </div>
                    <div className="col-span-3">
                      <div className="flex items-center gap-2">
                        <input value={item.description} onChange={e => updateItem(i, 'description', e.target.value)}
                          placeholder="Description"
                          className="flex-1 px-2 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:ring-1 focus:ring-blue-500/30" />
                        <span className={`text-[10px] whitespace-nowrap px-1.5 py-0.5 rounded ${
                          item.type === 'time' ? 'bg-blue-100 text-blue-600' : item.type === 'fixed' ? 'bg-purple-100 text-purple-600' : 'bg-slate-200 text-slate-600 dark:text-slate-300'
                        }`}>{item.type === 'time' ? 'Time' : item.type === 'fixed' ? 'Fixed' : 'Manual'}</span>
                      </div>
                    </div>
                    <div className="col-span-2">
                      <input type="number" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:ring-1 focus:ring-blue-500/30"
                        step="0.01" min="0" />
                    </div>
                    <div className="col-span-2">
                      <input type="number" value={item.rate} onChange={e => updateItem(i, 'rate', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none focus:ring-1 focus:ring-blue-500/30"
                        step="0.01" min="0" placeholder={item.type === 'fixed' ? 'Total' : 'Rate'} />
                    </div>
                    <div className="col-span-2 text-right font-semibold text-sm text-slate-900 dark:text-white py-1.5 pr-1">
                      {formatCurrency(item.amount, form.currency)}
                    </div>
                    <div className="col-span-1 text-center py-1">
                      <button onClick={() => removeItem(i)} className="p-1 hover:bg-red-50 dark:bg-red-900/30 rounded text-slate-400 hover:text-red-500">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5">
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Notes / Payment Terms</label>
            <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={3} placeholder="Payment terms, bank details, thank you note..."
              className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-none" />
          </div>
        </div>

        {/* Right: Summary & Billing Info */}
        <div className="space-y-6">
          {/* Totals */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Summary</h3>
            {(() => {
              const selectedClient = clients.find(c => c.id === form.client_id);
              const isOverseas = selectedClient?.gst_treatment === 'overseas';
              return (
                <>
                  {isOverseas && (
                    <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-xs text-blue-700 font-medium">
                      Overseas client — Tax not applicable
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400">Subtotal</span>
                    <span className="font-medium">{formatCurrency(subtotal, form.currency)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">Discount (%)</label>
                    <input type="number" value={form.discount_percent} onChange={e => setForm(f => ({ ...f, discount_percent: parseFloat(e.target.value) || 0 }))}
                      className="w-20 px-2 py-1 text-sm text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none" min="0" max="100" />
                    <span className="text-sm font-medium ml-auto text-red-500">-{formatCurrency(discountAmt, form.currency)}</span>
                  </div>
                  {!isOverseas && (
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">{form.tax_label} (%)</label>
                      <input type="number" value={form.tax_rate} onChange={e => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))}
                        className="w-20 px-2 py-1 text-sm text-right border border-slate-200 dark:border-slate-700 rounded focus:outline-none" min="0" />
                      <span className="text-sm font-medium ml-auto">{formatCurrency(taxAmt, form.currency)}</span>
                    </div>
                  )}
                  <div className="border-t border-slate-200 dark:border-slate-700 pt-3 flex justify-between">
                    <span className="font-semibold text-slate-900 dark:text-white">Total</span>
                    <span className="text-lg font-bold text-slate-900 dark:text-white">{formatCurrency(total, form.currency)}</span>
                  </div>
                </>
              );
            })()}
            <div className="pt-2">
              <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Currency</label>
              <select value={form.currency} onChange={e => {
                const newCurr = e.target.value;
                setForm(f => ({ ...f, currency: newCurr, exchange_rate: newCurr === companyCurrency ? 1 : f.exchange_rate }));
              }}
                className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                data-testid="invoice-currency-select">
                <option value="INR">INR - Indian Rupee</option>
                <option value="USD">USD - US Dollar</option>
                <option value="EUR">EUR - Euro</option>
                <option value="GBP">GBP - British Pound</option>
                <option value="AED">AED - UAE Dirham</option>
                <option value="SGD">SGD - Singapore Dollar</option>
                <option value="AUD">AUD - Australian Dollar</option>
                <option value="CAD">CAD - Canadian Dollar</option>
              </select>
            </div>
            {form.currency !== companyCurrency && (
              <div className="pt-1">
                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                  Exchange Rate (1 {form.currency} = ? {companyCurrency})
                </label>
                <input
                  type="number"
                  value={form.exchange_rate}
                  onChange={e => setForm(f => ({ ...f, exchange_rate: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  min="0"
                  step="0.01"
                  data-testid="exchange-rate-input"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Total in {companyCurrency}: {formatCurrency(total * (form.exchange_rate || 1), companyCurrency)}
                </p>
              </div>
            )}
          </div>

          {/* From Info */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm">From (Your Business)</h3>
            <input value={form.from_name} onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))}
              placeholder="Business name" className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none" />
            <input value={form.from_email} onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))}
              placeholder="Email" className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none" />
            <textarea value={form.from_address} onChange={e => setForm(f => ({ ...f, from_address: e.target.value }))}
              placeholder="Address" rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none resize-none" />
            <input value={form.from_gstin} onChange={e => setForm(f => ({ ...f, from_gstin: e.target.value }))}
              placeholder="GSTIN" className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none" />
          </div>

          {/* To Info */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
            <h3 className="font-semibold text-slate-900 dark:text-white text-sm">Bill To (Client)</h3>
            <input value={form.to_name} onChange={e => setForm(f => ({ ...f, to_name: e.target.value }))}
              placeholder="Client name" className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none" />
            <input value={form.to_email} onChange={e => setForm(f => ({ ...f, to_email: e.target.value }))}
              placeholder="Client email" className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none" />
            <textarea value={form.to_address} onChange={e => setForm(f => ({ ...f, to_address: e.target.value }))}
              placeholder="Client address" rows={2} className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none resize-none" />
            <input value={form.to_gstin} onChange={e => setForm(f => ({ ...f, to_gstin: e.target.value }))}
              placeholder="Client GSTIN" className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none" />
          </div>
        </div>
      </div>

      {/* Import Time Modal */}
      {showImport && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden" data-testid="import-time-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-lg text-slate-900 dark:text-white">Import Billable Time Entries</h3>
              <button onClick={() => setShowImport(false)} className="p-1 hover:bg-slate-100 dark:bg-slate-700 rounded"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto max-h-[60vh]">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">From Date</label>
                  <input type="date" value={importDateFrom} onChange={e => setImportDateFrom(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">To Date</label>
                  <input type="date" value={importDateTo} onChange={e => setImportDateTo(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg" />
                </div>
              </div>
              <button onClick={fetchBillableEntries} className="px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 rounded-lg text-sm font-medium">
                Refresh Entries
              </button>
              <div className="flex items-start gap-6">
                <div>
                  <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Group By</label>
                  <div className="flex gap-2">
                    {['project', 'member'].map(g => (
                      <button key={g} onClick={() => setImportGroupBy(g)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium ${importGroupBy === g ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300'}`}>
                        {g.charAt(0).toUpperCase() + g.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                {importData.projects.length > 0 && (
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Filter by Project</label>
                    <div className="flex flex-wrap gap-1.5">
                      {importData.projects.map(p => {
                        const isSelected = selectedProjectIds.includes(p.id);
                        return (
                          <button
                            key={p.id}
                            onClick={() => toggleProjectFilter(p.id)}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                              isSelected
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-blue-300'
                            }`}
                            data-testid={`project-filter-${p.id}`}
                          >
                            {p.name}
                          </button>
                        );
                      })}
                      {selectedProjectIds.length > 0 && (
                        <button
                          onClick={() => setSelectedProjectIds([])}
                          className="px-2.5 py-1 rounded-full text-xs font-medium text-slate-400 hover:text-slate-600 dark:text-slate-300 border border-dashed border-slate-300"
                          data-testid="clear-project-filter"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {importLoading ? (
                <p className="text-sm text-slate-400 text-center py-4">Loading entries...</p>
              ) : importData.entries.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-sm text-slate-400">No uninvoiced billable entries found for this client.</p>
                  {importData.already_invoiced_count > 0 && (
                    <p className="text-xs text-amber-500 mt-1">{importData.already_invoiced_count} entries are already on existing invoices.</p>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                    {selectedProjectIds.length > 0 ? `${filteredImportEntries.length} of ${importData.entries.length}` : importData.entries.length} billable entries
                    ({filteredImportEntries.reduce((s, e) => s + e.hours, 0).toFixed(2)} total hours)
                  </p>
                  <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs text-slate-500 dark:text-slate-400">Project</th>
                          <th className="text-left px-3 py-2 text-xs text-slate-500 dark:text-slate-400">Member</th>
                          <th className="text-left px-3 py-2 text-xs text-slate-500 dark:text-slate-400">Role</th>
                          <th className="text-right px-3 py-2 text-xs text-slate-500 dark:text-slate-400">Hours</th>
                          <th className="text-right px-3 py-2 text-xs text-slate-500 dark:text-slate-400">Rate</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredImportEntries.map((e, i) => (
                          <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                            <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{e.project_name}</td>
                            <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{e.user_name}</td>
                            <td className="px-3 py-2 text-slate-400 text-xs">{e.user_role || '—'}</td>
                            <td className="px-3 py-2 text-right">{e.hours}h</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(e.hourly_rate, form.currency)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
              <button onClick={() => setShowImport(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:bg-slate-700 rounded-lg">Cancel</button>
              <button onClick={importTimeEntries} disabled={filteredImportEntries.length === 0}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                data-testid="confirm-import-btn">
                Import {filteredImportEntries.length} Entries
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Invoice Detail View ----
const InvoiceDetail = ({ invoice, onBack, onEdit, onStatusChange, onDownload }) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-slate-100 dark:bg-slate-700 rounded-lg" data-testid="back-from-detail">
          <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-300" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{invoice.invoice_number}</h1>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium mt-1 ${STATUS_CONFIG[invoice.status]?.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_CONFIG[invoice.status]?.dot}`}></span>
            {STATUS_CONFIG[invoice.status]?.label}
          </span>
        </div>
        <div className="flex gap-2">
          {invoice.status === 'draft' && (
            <>
              <button onClick={() => onEdit(invoice)} className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm hover:bg-slate-50 dark:bg-slate-700" data-testid="edit-invoice-btn">
                <Edit className="w-4 h-4" /> Edit
              </button>
              <button onClick={() => onStatusChange(invoice.id, 'sent')} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700" data-testid="mark-sent-btn">
                <Send className="w-4 h-4" /> Mark as Sent
              </button>
            </>
          )}
          {invoice.status === 'sent' && (
            <button onClick={() => onStatusChange(invoice.id, 'paid')} className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700" data-testid="mark-paid-btn">
              <CheckCircle className="w-4 h-4" /> Mark as Paid
            </button>
          )}
          <button onClick={() => onDownload(invoice)} className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm hover:bg-slate-50 dark:bg-slate-700" data-testid="download-invoice-btn">
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      {/* Invoice Preview Card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 max-w-4xl" id="invoice-preview" data-testid="invoice-preview">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div className="flex items-start gap-4">
            {invoice.from_logo && (
              <img src={invoice.from_logo} alt="Company Logo" className="w-14 h-14 object-contain rounded" style={{maxWidth:'56px',maxHeight:'56px'}} />
            )}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">INVOICE</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">{invoice.invoice_number}</p>
            </div>
          </div>
          <div className="text-right text-sm text-slate-600 dark:text-slate-300">
            <p><span className="text-slate-400">Issue Date:</span> {invoice.issue_date}</p>
            <p><span className="text-slate-400">Due Date:</span> {invoice.due_date || '—'}</p>
          </div>
        </div>

        {/* From / To */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase mb-2">From</p>
            <p className="font-semibold text-slate-900 dark:text-white">{invoice.from_name || '—'}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{invoice.from_email}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 whitespace-pre-line">{invoice.from_address}</p>
            {invoice.from_gstin && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">GSTIN: {invoice.from_gstin}</p>}
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Bill To</p>
            <p className="font-semibold text-slate-900 dark:text-white">{invoice.to_name || invoice.client_name || '—'}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400">{invoice.to_email}</p>
            <p className="text-sm text-slate-500 dark:text-slate-400 whitespace-pre-line">{invoice.to_address}</p>
            {invoice.to_gstin && <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">GSTIN: {invoice.to_gstin}</p>}
          </div>
        </div>

        {/* Items Table */}
        <table className="w-full mb-6">
          <thead>
            <tr className="border-b-2 border-slate-200 dark:border-slate-700">
              <th className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase py-3">#</th>
              <th className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase py-3">Project</th>
              <th className="text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase py-3">Description</th>
              <th className="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase py-3">Qty</th>
              <th className="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase py-3">Rate</th>
              <th className="text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase py-3">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.items || []).map((item, i) => (
              <tr key={i} className="border-b border-slate-100 dark:border-slate-700">
                <td className="py-3 text-sm text-slate-400">{i + 1}</td>
                <td className="py-3 text-sm text-slate-600 dark:text-slate-300">{item.project_name || '—'}</td>
                <td className="py-3 text-sm text-slate-700 dark:text-slate-200">{item.description}</td>
                <td className="py-3 text-sm text-slate-600 dark:text-slate-300 text-right">{item.quantity} {item.unit !== 'fixed' ? item.unit : ''}</td>
                <td className="py-3 text-sm text-slate-600 dark:text-slate-300 text-right">{item.type !== 'fixed' ? formatCurrency(item.rate, invoice.currency) : '—'}</td>
                <td className="py-3 text-sm text-slate-900 dark:text-white font-medium text-right">{formatCurrency(item.amount, invoice.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-64 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-slate-400">Subtotal</span><span>{formatCurrency(invoice.subtotal, invoice.currency)}</span></div>
            {invoice.discount_percent > 0 && (
              <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-slate-400">Discount ({invoice.discount_percent}%)</span><span className="text-red-500">-{formatCurrency(invoice.discount_amount, invoice.currency)}</span></div>
            )}
            {invoice.tax_rate > 0 && (
              <div className="flex justify-between text-sm"><span className="text-slate-500 dark:text-slate-400">{invoice.tax_label} ({invoice.tax_rate}%)</span><span>{formatCurrency(invoice.tax_amount, invoice.currency)}</span></div>
            )}
            <div className="flex justify-between font-bold text-lg border-t border-slate-200 dark:border-slate-700 pt-2">
              <span>Total</span><span>{formatCurrency(invoice.total, invoice.currency)}</span>
            </div>
          </div>
        </div>

        {invoice.notes && (
          <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs font-semibold text-slate-400 uppercase mb-1">Notes</p>
            <p className="text-sm text-slate-600 dark:text-slate-300 whitespace-pre-line">{invoice.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ---- Main Invoices Page ----
export default function Invoices() {
  const { token } = useAuth();
  const [view, setView] = useState('list'); // list, create, edit, detail
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyCurrency, setCompanyCurrency] = useState('INR');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setInvoices(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  const fetchCompanyCurrency = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/workspace-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCompanyCurrency(data.company_currency || 'INR');
      }
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => { fetchInvoices(); fetchCompanyCurrency(); }, [fetchInvoices, fetchCompanyCurrency]);

  const handleSave = async (formData) => {
    const isEdit = view === 'edit' && selectedInvoice;
    const url = isEdit ? `${API}/api/invoices/${selectedInvoice.id}` : `${API}/api/invoices`;
    const method = isEdit ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        const saved = await res.json();
        await fetchInvoices();
        setSelectedInvoice(saved);
        setView('detail');
      }
    } catch (e) { console.error(e); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const res = await fetch(`${API}/api/invoices/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (res.ok) {
        await fetchInvoices();
        if (selectedInvoice?.id === id) {
          setSelectedInvoice(prev => ({ ...prev, status }));
        }
      }
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this invoice? This action cannot be undone.')) return;
    try {
      await fetch(`${API}/api/invoices/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchInvoices();
    } catch (e) { console.error(e); }
  };

  const handleDownloadPDF = (invoice) => {
    const el = document.getElementById('invoice-preview');
    if (!el) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html><head><title>${invoice.invoice_number}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 40px; color: #1e293b; font-size: 13px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 10px 12px; text-align: left; vertical-align: top; }
        th { border-bottom: 2px solid #e2e8f0; font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 600; }
        td { border-bottom: 1px solid #f1f5f9; }
        img { max-width: 56px; max-height: 56px; object-fit: contain; border-radius: 4px; }
        .flex { display: flex; }
        .justify-between { justify-content: space-between; }
        .justify-end { justify-content: flex-end; }
        .items-start { align-items: flex-start; }
        .items-center { align-items: center; }
        .gap-4 { gap: 16px; }
        .gap-8 { gap: 32px; }
        .text-right { text-align: right !important; }
        .text-left { text-align: left; }
        .font-bold { font-weight: 700; }
        .font-semibold { font-weight: 600; }
        .font-medium { font-weight: 500; }
        .text-sm { font-size: 13px; }
        .text-xs { font-size: 11px; }
        .text-lg { font-size: 18px; }
        .text-2xl { font-size: 24px; }
        .text-slate-400 { color: #94a3b8; }
        .text-slate-500 dark:text-slate-400 { color: #64748b; }
        .text-slate-600 dark:text-slate-300 { color: #475569; }
        .text-slate-700 dark:text-slate-200 { color: #334155; }
        .text-slate-900 dark:text-white { color: #0f172a; }
        .text-red-500 { color: #ef4444; }
        .mb-1 { margin-bottom: 4px; }
        .mb-2 { margin-bottom: 8px; }
        .mb-6 { margin-bottom: 24px; }
        .mb-8 { margin-bottom: 32px; }
        .mt-1 { margin-top: 4px; }
        .mt-8 { margin-top: 32px; }
        .pt-2 { padding-top: 8px; }
        .pt-6 { padding-top: 24px; }
        .py-3 { padding-top: 12px; padding-bottom: 12px; }
        .space-y-2 > * + * { margin-top: 8px; }
        .w-64 { width: 260px; }
        .whitespace-pre-line { white-space: pre-line; }
        .border-t { border-top: 1px solid #e2e8f0; }
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
        .uppercase { text-transform: uppercase; }
        .totals-row { display: flex; justify-content: space-between; font-size: 13px; }
        .totals-row span:first-child { color: #64748b; }
        .totals-total { display: flex; justify-content: space-between; font-weight: 700; font-size: 18px; border-top: 2px solid #e2e8f0; padding-top: 8px; }
        @media print { body { padding: 20px; } }
      </style></head><body>${el.innerHTML}</body></html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (view === 'create') {
    return <Layout><InvoiceForm onSave={handleSave} onCancel={() => setView('list')} token={token} companyCurrency={companyCurrency} /></Layout>;
  }
  if (view === 'edit' && selectedInvoice) {
    return <Layout><InvoiceForm invoice={selectedInvoice} onSave={handleSave} onCancel={() => setView('detail')} token={token} companyCurrency={companyCurrency} /></Layout>;
  }
  if (view === 'detail' && selectedInvoice) {
    return (
      <Layout>
        <InvoiceDetail
          invoice={selectedInvoice}
          onBack={() => { setView('list'); setSelectedInvoice(null); }}
          onEdit={(inv) => { setSelectedInvoice(inv); setView('edit'); }}
          onStatusChange={handleStatusChange}
          onDownload={handleDownloadPDF}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <InvoiceList
        invoices={invoices}
        loading={loading}
        onCreate={() => setView('create')}
        onView={(inv) => { setSelectedInvoice(inv); setView('detail'); }}
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
        companyCurrency={companyCurrency}
      />
    </Layout>
  );
}
